import type { TicketMessage } from '@/types/ticket'
import { SystemMessageRow } from './SystemMessageRow'
import { AttachmentList } from '../attachments/AttachmentList'
import { AvatarInitials } from '@/components/ui/avatar-initials'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { EtainablBadge } from '@/components/etainabl-mark'
import { formatDateTime, formatDayLabel, formatTime } from '@/lib/dates'

function authorName(message: TicketMessage): string {
  return message.author?.name ?? 'Unknown'
}

function isStaff(message: TicketMessage): boolean {
  return Boolean(message.author?.isAdmin) || message.authorRole === 'admin'
}

// Lighter, avatar-led rows for the tracker detail's Comments section (design
// doc "Tracked issue detail") — the ticket-style colored/indented bubbles
// read as too heavy once the property band and Description card already
// carry the page's visual weight. Meta line is gap-spaced with no "·"
// joiners, per the approved mockup.
function CommentRow({ ticketId, message }: { ticketId: string; message: TicketMessage }) {
  const staff = isStaff(message)
  const pending = message._id.startsWith('optimistic-')
  return (
    <div className={`flex gap-2.5 border-b border-border py-3 last:border-b-0 ${pending ? 'opacity-60' : ''}`}>
      <AvatarInitials name={authorName(message)} size="md" className="shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-etn-micro text-etn-text-soft">
          <span className="font-semibold text-etn-control text-etn-text-cell">{authorName(message)}</span>
          {staff && <EtainablBadge />}
          {message.isInternal && <span className="font-medium text-amber-700">Internal note</span>}
          <span>{formatDateTime(message.createdAt)}</span>
        </div>
        <div className="prose prose-sm dark:prose-invert mt-1 max-w-none text-etn-text-cell">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.body}</ReactMarkdown>
        </div>
        <AttachmentList ticketId={ticketId} attachments={message.attachments} />
      </div>
    </div>
  )
}

// Uppercase micro label between hairlines, grouping the thread by calendar
// day so individual messages only need a time.
function DayDivider({ label }: { label: string }) {
  return (
    <div
      className="my-4 flex items-center gap-3 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-soft before:h-px before:flex-1 before:bg-etn-border-row after:h-px after:flex-1 after:bg-etn-border-row"
      data-testid="day-divider"
    >
      {label}
    </div>
  )
}

function MessageRow({ ticketId, message }: { ticketId: string; message: TicketMessage }) {
  const staff = isStaff(message)
  // Optimistic messages (still in flight) render dimmed until the server
  // confirms and the refetch swaps in the real document.
  const pending = message._id.startsWith('optimistic-')
  // Who said what has to be readable without reading: customer messages sit
  // left on a white card, staff replies sit right on a green tint. Internal
  // notes keep their own amber card — they are staff-side too, so they stay on
  // the right, but must never be mistakable for something the customer saw.
  const cardClass = message.isInternal
    ? 'bg-etn-warning-subtle border-etn-warning-text/25'
    : staff
    ? 'bg-etn-accent-subtle border-etn-accent-subtle-border'
    : 'bg-etn-surface border-etn-border-hairline'

  return (
    <div
      className={`mb-3.5 flex max-w-[82%] gap-2.5 ${staff ? 'ml-auto flex-row-reverse' : ''} ${pending ? 'opacity-60' : ''}`}
      data-testid="message-row"
      data-author={staff ? 'staff' : 'customer'}
    >
      <AvatarInitials name={authorName(message)} size="md" className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className={`rounded-etn-card border px-3.5 py-2.5 shadow-etn-whisper ${cardClass}`}>
          <div className="mb-1 flex flex-wrap items-baseline gap-2 text-etn-micro text-etn-text-soft">
            <span className="text-etn-control font-semibold text-etn-text-cell">{authorName(message)}</span>
            {staff && (
              <span className="rounded-etn-pill bg-etn-accent-tint px-[7px] py-0.5 text-[9px] font-bold tracking-[0.06em] text-etn-accent-text-active">
                STAFF
              </span>
            )}
            {message.isInternal && <span className="font-semibold text-etn-warning-text">Internal note</span>}
            <span>{formatTime(message.createdAt)}</span>
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none text-etn-text-cell">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.body}</ReactMarkdown>
          </div>
          <AttachmentList ticketId={ticketId} attachments={message.attachments} />
        </div>
      </div>
    </div>
  )
}

export function MessageThread({
  ticketId,
  messages,
  variant = 'thread',
}: {
  ticketId: string
  messages: TicketMessage[]
  // 'thread' (default) is the BAU ticket route's conversation layout —
  // side-by-side cards under day dividers. 'comments' is the tracker detail's
  // lighter comment list — see CommentRow above.
  variant?: 'thread' | 'comments'
}) {
  if (variant === 'comments') {
    return (
      <div>
        {messages.map(m =>
          m.kind === 'system'
            ? <SystemMessageRow key={m._id} message={m} />
            : <CommentRow key={m._id} ticketId={ticketId} message={m} />,
        )}
      </div>
    )
  }

  let lastDay: string | null = null

  return (
    <div className="px-6 py-4">
      {messages.map(m => {
        const day = formatDayLabel(m.createdAt)
        const newDay = day !== lastDay
        lastDay = day
        return (
          <div key={m._id}>
            {newDay && <DayDivider label={day} />}
            {m.kind === 'system'
              ? <SystemMessageRow message={m} />
              : <MessageRow ticketId={ticketId} message={m} />}
          </div>
        )
      })}
    </div>
  )
}
